import React, { useEffect, useState } from "react";
import { Heart, Plus, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

const resolvePhoto = (p) => {
  if (!p) return null;
  if (p.startsWith("http")) return p;
  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
};

export default function WishlistPickerModal({ listing, onClose }) {
  const { user } = useAuth();
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/me/wishlists").then(({ data }) => setWishlists(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const isInList = (wl) => (wl.listing_ids || []).includes(listing.id);

  const toggle = async (wl) => {
    setBusyId(wl.id);
    try {
      if (isInList(wl)) {
        await api.delete(`/me/wishlists/${wl.id}/listings/${listing.id}`);
        setWishlists((arr) => arr.map((w) => w.id === wl.id ? { ...w, listing_ids: (w.listing_ids || []).filter((x) => x !== listing.id) } : w));
        toast.success(`Retiré de « ${wl.name} »`);
      } else {
        await api.post(`/me/wishlists/${wl.id}/listings`, { listing_id: listing.id });
        setWishlists((arr) => arr.map((w) => w.id === wl.id ? { ...w, listing_ids: [...(w.listing_ids || []), listing.id] } : w));
        toast.success(`Ajouté à « ${wl.name} »`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusyId(null); }
  };

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/me/wishlists", { name: newName.trim(), listing_id: listing.id });
      setWishlists((arr) => [data, ...arr]);
      setNewName("");
      setShowNew(false);
      toast.success(`Liste « ${data.name} » créée`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setCreating(false); }
  };

  if (!user) {
    return (
      <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
          <div className="font-display text-xl font-bold mb-3">Connectez-vous</div>
          <p className="text-sm text-neutral-600 mb-5">Créez un compte pour enregistrer vos coups de cœur dans des listes nommées.</p>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold border border-black/15">Plus tard</button>
            <Link to="/login" className="px-4 py-2 text-sm font-semibold bg-[#0A0A0A] text-white" onClick={onClose}>Se connecter</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="wishlist-picker">
      <div className="bg-white max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-5 border-b border-black/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#7A1535]">Coups de cœur</div>
            <div className="font-display text-xl font-bold mt-0.5">Enregistrer dans une liste</div>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="p-2 hover:bg-neutral-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <>
              {wishlists.length === 0 && !showNew && (
                <div className="text-center py-6 text-sm text-neutral-500">
                  Aucune liste pour le moment. Créez votre première !
                </div>
              )}
              <div className="space-y-2">
                {wishlists.map((wl) => {
                  const in_ = isInList(wl);
                  const cover = wl.preview?.[0]?.photos?.[0];
                  return (
                    <button
                      key={wl.id}
                      type="button"
                      disabled={busyId === wl.id}
                      onClick={() => toggle(wl)}
                      data-testid={`wl-item-${wl.id}`}
                      className={`w-full flex items-center gap-3 p-2 border rounded-lg text-left hover:bg-neutral-50 transition-colors ${in_ ? "border-[#7A1535] bg-[#FBF6F8]" : "border-black/10"}`}
                    >
                      <div className="w-14 h-14 rounded bg-neutral-100 overflow-hidden shrink-0">
                        {cover ? (
                          <img src={resolvePhoto(cover)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-neutral-400">
                            <Heart className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{wl.name}</div>
                        <div className="text-xs text-neutral-500">{wl.count} logement{wl.count !== 1 ? "s" : ""}</div>
                      </div>
                      {in_ && <Check className="w-5 h-5 text-[#7A1535] shrink-0" />}
                      {busyId === wl.id && <Loader2 className="w-4 h-4 animate-spin shrink-0" />}
                    </button>
                  );
                })}
              </div>

              {showNew ? (
                <div className="mt-4 border-t border-black/10 pt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Nouvelle liste</div>
                  <input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && create()}
                    placeholder="Ex : Été 2026, Weekend en amoureux…"
                    className="w-full border border-black/15 px-3 py-2 text-sm"
                    data-testid="wl-new-name"
                  />
                  <div className="flex justify-end gap-2 mt-3">
                    <button onClick={() => { setShowNew(false); setNewName(""); }} className="px-3 py-1.5 text-sm font-semibold border border-black/15">Annuler</button>
                    <button onClick={create} disabled={creating || !newName.trim()} className="px-3 py-1.5 text-sm font-semibold bg-[#0A0A0A] text-white disabled:opacity-50" data-testid="wl-create-btn">
                      {creating ? "Création…" : "Créer"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNew(true)}
                  data-testid="wl-add-new"
                  className="mt-3 w-full p-3 border-2 border-dashed border-black/20 text-sm font-semibold hover:border-[#0A0A0A] inline-flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Créer une nouvelle liste
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
